import type { WatchlistDto } from '@/lib/api-client';

/** Panel source: 'all' = deduped union across every watchlist, otherwise a
 *  specific watchlist id. */
export type WatchlistSource = 'all' | string;

/** Row sort order for the panel. */
export type PanelSort = 'alpha' | 'change';

/** Selectable futures/indices for the panel's futures group. `key` is the
 *  stable id stored in config; `symbol` is the commodity_prices symbol
 *  (priced via getCommodityPrices). NDX = Nasdaq-100 futures (NQ=F). */
export const FUTURES: ReadonlyArray<{ key: string; symbol: string; label: string }> = [
  { key: 'NDX', symbol: 'NQ=F', label: 'NDX' },
  { key: 'OIL', symbol: 'CL=F', label: 'Oil' },
  { key: 'GAS', symbol: 'NG=F', label: 'Gas' },
  { key: 'GOLD', symbol: 'GC=F', label: 'Gold' },
  { key: 'SILVER', symbol: 'SI=F', label: 'Silver' },
];

/** All future keys (default selection). */
export const FUTURE_KEYS: readonly string[] = FUTURES.map((f) => f.key);

/** Symbol set for detecting a tracked future amongst watchlist tickers. */
export const TRACKED_FUTURES: ReadonlySet<string> = new Set(FUTURES.map((f) => f.symbol));

/** Tickers to show for the chosen source. 'all' = deduped union across all
 *  watchlists in first-seen order; otherwise just the named list's items.
 *  Tickers are upper-cased and blanks dropped. */
export function resolvePanelTickers(watchlists: WatchlistDto[], source: WatchlistSource): string[] {
  const lists = source === 'all' ? watchlists : watchlists.filter((w) => w.id === source);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const w of lists) {
    for (const it of w.items) {
      const t = (it.ticker ?? '').toUpperCase().trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

/** Minimal watchlist item shape shared by the dashboard tile
 *  (`WatchlistContent`) and the `useWatchlist` hook. */
export interface WatchItemLike { ticker: string; addedAt: number }

/** Insert `ticker` at the TOP of a list's items (newest-first), deduped.
 *  Both add paths — the dashboard tile's AddTickerInput and the WatchButton
 *  star picker — funnel through this so a freshly-added stock always lands at
 *  the top of the list rather than the bottom. Returns the SAME array
 *  reference when the ticker is already present, letting callers skip a
 *  redundant persist/sync. Matching is case-sensitive; callers upper-case the
 *  symbol before adding. */
export function addTickerAtTop<T extends WatchItemLike>(items: T[], ticker: string, now: number): T[] {
  if (items.some((i) => i.ticker === ticker)) return items;
  return [{ ticker, addedAt: now } as T, ...items];
}

/** Sort priced rows. 'manual' keeps fetch (watchlist) order, 'alpha' sorts by
 *  ticker A→Z, 'change' sorts by % change high→low (nulls last). Pure + stable. */
export function sortPanelRows<T extends { ticker: string; changePct: number | null }>(
  rows: T[],
  sort: PanelSort,
): T[] {
  if (sort === 'change') {
    return [...rows].sort((a, b) => (b.changePct ?? -Infinity) - (a.changePct ?? -Infinity));
  }
  // default 'alpha'
  return [...rows].sort((a, b) => a.ticker.localeCompare(b.ticker));
}

/** First alphanumeric character of a ticker, upper-cased — used for the
 *  fallback avatar when no logo exists (TQQQ → "T"). */
export function initialFor(ticker: string): string {
  const m = (ticker ?? '').match(/[A-Za-z0-9]/);
  return m ? m[0].toUpperCase() : '?';
}

export type PanelMarketSession = 'pre-market' | 'regular' | 'after-hours' | 'closed';

export interface PanelQuote {
  price: number | null;
  changePct: number | null;
  extendedHoursPrice?: number | null;
  extendedHoursChangePct?: number | null;
}

/** Choose which price/change to display for the current session. In
 *  pre-market / after-hours, prefer the extended-hours quote when present;
 *  otherwise fall back to the regular (last) quote. */
export function pickDisplayQuote(
  q: PanelQuote,
  session: PanelMarketSession,
): { price: number | null; changePct: number | null; extended: boolean } {
  const useExt = (session === 'pre-market' || session === 'after-hours') && q.extendedHoursPrice != null;
  if (useExt) {
    return {
      price: q.extendedHoursPrice ?? null,
      changePct: q.extendedHoursChangePct ?? null,
      extended: true,
    };
  }
  return { price: q.price ?? null, changePct: q.changePct ?? null, extended: false };
}
